import { TEACHERS_STUDENTS_UNIFIED } from '../config/Config';

export function mapPlatformRelationship(r) {
  const subjName = r.subject_name || r.subject?.name || '';
  const subjId = r.subject_pk ?? r.subject_id ?? r.subject ?? '';
  return {
    source_type: 'platform',
    row_key: `platform-${r.id}`,
    id: `platform-${r.id}`,
    relationship_id: r.id,
    managed_student_id: r.managed_student_id ?? null,
    display_name: null,
    student_pk: r.student,
    email: r.student_email || '',
    first_name: r.student_name ? r.student_name.split(' ')[0] || '' : '',
    last_name: r.student_name ? r.student_name.split(' ').slice(1).join(' ') || '' : '',
    subject_id: subjId,
    subject_name: subjName,
    subjects: subjName,
    joined_date: r.date_joined,
    is_active: r.is_active,
    academic_year_id: r.academic_year || '',
    academic_year_label: '',
    award_count: r.award_count || 0,
    ended_by_email: r.ended_by_email || null,
    ended_at: r.ended_at || null,
    is_accepted: r.is_accepted || false,
    invitation_status: r.invitation_status || 'not_invited',
    notes: r.notes || '',
    managed_status: null,
    dashboard_status: r.dashboard_status || null,
  };
}

export function mapManagedStudent(m) {
  const display = (m.display_name || '').trim();
  const parts = display.split(/\s+/).filter(Boolean);
  const first_name = parts[0] || '';
  const last_name = parts.slice(1).join(' ') || '';
  const subjName = m.subject_name || '';
  let invitation_status = 'not_invited';
  if (m.status === 'invited_pending') invitation_status = 'awaiting';
  const linkedPk =
    m.linked_student == null || m.linked_student === ''
      ? null
      : typeof m.linked_student === 'object' && m.linked_student.id != null
        ? m.linked_student.id
        : m.linked_student;
  if (m.status === 'onboarded_linked' && linkedPk) invitation_status = 'yes';

  return {
    source_type: 'managed_student',
    row_key: `managed-${m.id}`,
    id: `managed-${m.id}`,
    relationship_id: null,
    managed_student_id: m.id,
    display_name: m.display_name || '',
    student_pk: linkedPk,
    email: m.email || m.last_invited_email || '',
    first_name,
    last_name,
    subject_id: m.subject ?? '',
    subject_name: subjName,
    subjects: subjName,
    joined_date: m.created_at,
    is_active: true,
    academic_year_id: m.academic_year ?? '',
    academic_year_label: '',
    award_count: 0,
    ended_by_email: null,
    ended_at: null,
    is_accepted: null,
    invitation_status,
    notes: m.notes || '',
    managed_status: m.status,
    dashboard_status: m.dashboard_status,
  };
}

/** Normalize API `linked_student` (pk or nested { id }) for Set lookups. */
export function extractLinkedStudentIdFromManagedApi(m) {
  const v = m?.linked_student;
  if (v == null || v === '') return null;
  if (typeof v === 'object' && v.id != null) return String(v.id);
  return String(v);
}

/**
 * Multiple TEACHER relationships can exist per platform student (e.g. different subjects).
 * For roster / assignment filters, show one row per logical student (same user id).
 */
export function mergePlatformStudentsByUserId(platformRows) {
  const byPk = new Map();
  const unkeyed = [];
  for (const row of platformRows) {
    const pk = row.student_pk;
    if (pk == null || pk === '') {
      unkeyed.push(row);
      continue;
    }
    const key = String(pk);
    if (!byPk.has(key)) byPk.set(key, []);
    byPk.get(key).push(row);
  }
  const merged = [];
  for (const [key, rows] of byPk) {
    if (rows.length === 1) {
      merged.push(rows[0]);
      continue;
    }
    const sorted = [...rows].sort((a, b) => {
      if (Boolean(a.is_active) !== Boolean(b.is_active)) return a.is_active ? -1 : 1;
      const da = a.joined_date ? new Date(a.joined_date).getTime() : 0;
      const db = b.joined_date ? new Date(b.joined_date).getTime() : 0;
      return db - da;
    });
    const primary = sorted[0];
    const awardTotal = sorted.reduce((sum, r) => sum + (Number(r.award_count) || 0), 0);
    const anyActive = sorted.some((r) => r.is_active);
    const invitationRank = (inv) => {
      const v = String(inv || '').toLowerCase();
      if (v === 'yes') return 4;
      if (v === 'awaiting') return 3;
      if (v === 'not_invited') return 2;
      if (v === 'no' || v === 'rejected') return 1;
      if (v === 'exited') return 1;
      return 0;
    };
    const acceptanceRank = (a) => {
      const v = String(a || '').toLowerCase();
      if (v === 'accepted') return 3;
      if (v === 'awaiting') return 2;
      return 1;
    };
    let bestInvitation = primary.invitation_status;
    let bestInvRank = invitationRank(bestInvitation);
    let bestAccepted = primary.is_accepted;
    let bestAccRank = acceptanceRank(bestAccepted);
    for (const r of sorted) {
      const ir = invitationRank(r.invitation_status);
      if (ir > bestInvRank) {
        bestInvRank = ir;
        bestInvitation = r.invitation_status;
      }
      const ar = acceptanceRank(r.is_accepted);
      if (ar > bestAccRank) {
        bestAccRank = ar;
        bestAccepted = r.is_accepted;
      }
    }
    const subjParts = [];
    const seen = new Set();
    for (const r of sorted) {
      const bits = String(r.subjects || r.subject_name || '')
        .split(/[,;/]/)
        .map((s) => s.trim())
        .filter(Boolean);
      for (const b of bits) {
        const low = b.toLowerCase();
        if (!seen.has(low)) {
          seen.add(low);
          subjParts.push(b);
        }
      }
    }
    const subjectsJoined = subjParts.join(', ') || primary.subjects || '';
    merged.push({
      ...primary,
      row_key: `platform-student-${key}`,
      id: `platform-student-${key}`,
      relationship_id: primary.relationship_id,
      subjects: subjectsJoined,
      subject_name: subjectsJoined,
      is_active: anyActive,
      award_count: awardTotal,
      invitation_status: bestInvitation,
      is_accepted: bestAccepted,
    });
  }
  return [...merged, ...unkeyed];
}

export async function fetchUnifiedTeacherStudents(api) {
  const response = await api.get(TEACHERS_STUDENTS_UNIFIED);
  const rel = response.data?.platform_relationships;
  const managed = response.data?.managed_students;
  const rawRel = Array.isArray(rel) ? rel : [];
  const rawManaged = Array.isArray(managed) ? managed : [];
  const platformStudentIds = new Set(
    rawRel
      .map((r) => r?.student)
      .filter((sid) => sid !== null && sid !== undefined && sid !== '')
      .map((sid) => String(sid))
  );
  const platformStudentEmails = new Set(
    rawRel
      .map((r) => (r?.student_email || '').trim().toLowerCase())
      .filter(Boolean)
  );
  const activeManaged = rawManaged.filter((m) => {
    const ls = extractLinkedStudentIdFromManagedApi(m);
    if (ls && platformStudentIds.has(ls)) {
      return false;
    }
    const managedEmail = (m.email || m.last_invited_email || '').trim().toLowerCase();
    if (managedEmail && platformStudentEmails.has(managedEmail)) {
      return false;
    }
    return true;
  });
  const platformMapped = rawRel.map(mapPlatformRelationship);
  const mergedPlatform = mergePlatformStudentsByUserId(platformMapped);
  return [...mergedPlatform, ...activeManaged.map(mapManagedStudent)];
}

export function isManagedRosterStudent(s) {
  return s?.source_type === 'managed_student';
}

/** Platform student user id, or null for roster-only rows. */
export function getPlatformStudentPk(s) {
  if (!s || isManagedRosterStudent(s)) return null;
  if (s.student_pk != null && s.student_pk !== '') {
    return Number(s.student_pk);
  }
  return (
    (s.student && typeof s.student === 'number' ? s.student : null) ??
    (s.student && typeof s.student === 'object' && s.student.id) ??
    s.user_id ??
    s.student_id ??
    null
  );
}

export function getManagedStudentRecordId(s) {
  if (!s || !isManagedRosterStudent(s)) return null;
  return s.managed_student_id ?? null;
}

/** Autocomplete / list label: email when present; otherwise roster display_name or combined name. */
export function formatTeacherStudentOptionLabel(option) {
  if (!option || typeof option !== 'object') return '';
  const email = (option.email || '').trim();
  const displayName = (option.display_name || '').trim();
  const name = (option.name || '').trim();
  return email || displayName || name || '';
}
